import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import { Inject, Injectable } from "@angular/core";
import { Roles, SystemConfigPromiseService } from "shared/cubiq-services";
import { NativeDesktopSupport } from "app/bfx-native-desktop/native-desktop-support";
import { SESSION_TIMESTAMP } from "../../../shared/broker-client-services/worker/CubiqBrokerClient.constants";
import { BfxUIConfiguration } from "../../../shared/auth-services/system-config/model/config.models";
import { DashboardMenuItem } from "../models/dashboard-menu-item";
import { BfxConfigService } from "../../../shared/auth-services/system-config/bfx-config.service";

// each pair should have a route mapping in admin-dashboard.constants.ts
const ADMIN_ROLES = [
  Roles.ADMIN_BRANCHES,
  Roles.ADMIN_BRANCHES_UPDATE,
  Roles.ADMIN_CLIENTS,
  Roles.ADMIN_CLIENTS_UPDATE,
  Roles.ADMIN_ACCOUNTS,
  Roles.ADMIN_ACCOUNTS_UPDATE,
  Roles.ADMIN_BREAK_ACCOUNTS,
  Roles.ADMIN_BREAK_ACCOUNTS_UPDATE,
  Roles.ADMIN_CLIENT_SETTINGS,
  Roles.ADMIN_CLIENT_SETTINGS_UPDATE,
  Roles.ADMIN_MARKUP_PROFILES,
  Roles.ADMIN_MARKUP_PROFILES_UPDATE,
  Roles.ADMIN_CLIENT_TRADING_PERMISSIONS,
  Roles.ADMIN_CLIENT_TRADING_PERMISSIONS_UPDATE,
  Roles.ADMIN_BENCHMARK_PRICING_PROFILE,
  Roles.ADMIN_BENCHMARK_PRICING_PROFILE_UPDATE,
  Roles.ADMIN_USER_SETUP,
  Roles.ADMIN_USER_SETUP_UPDATE,
  Roles.ADMIN_SALES_GROUP,
  Roles.ADMIN_SALES_GROUP_UPDATE,
  Roles.ADMIN_TRADER_GROUP,
  Roles.ADMIN_TRADER_GROUP_UPDATE,
  Roles.ADMIN_CUT_OFF_GROUP,
  Roles.ADMIN_CUT_OFF_GROUP_UPDATE,
  Roles.ADMIN_PRIVILEGE_GROUPS,
  Roles.ADMIN_PRIVILEGE_GROUPS_UPDATE,
  Roles.ADMIN_USER_TRADING_PERMISSIONS,
  Roles.ADMIN_USER_TRADING_PERMISSIONS_UPDATE,
  Roles.ADMIN_VISIBILITY_PROFILES,
  Roles.ADMIN_VISIBILITY_PROFILES_UPDATE,
  Roles.ADMIN_CURRENCY,
  Roles.ADMIN_CURRENCY_UPDATE,
  Roles.ADMIN_CURRENCY_PAIR,
  Roles.ADMIN_CURRENCY_PAIR_UPDATE,
  Roles.ADMIN_CURRENCY_PAIR_GROUP,
  Roles.ADMIN_CURRENCY_PAIR_GROUP_UPDATE,
  Roles.ADMIN_BENCHMARK_CODE,
  Roles.ADMIN_BENCHMARK_CODE_UPDATE,
  Roles.ADMIN_NDF_FIXING_CODE,
  Roles.ADMIN_NDF_FIXING_CODE_UPDATE,
  Roles.ADMIN_CALENDARS,
  Roles.ADMIN_CALENDARS_UPDATE,
  Roles.ADMIN_MARKET_TIMES,
  Roles.ADMIN_MARKET_TIMES_UPDATE,
  Roles.ADMIN_RATE_SOURCE_MANAGEMENT,
  Roles.ADMIN_RATE_SOURCE_MANAGEMENT_UPDATE,
  Roles.ADMIN_MANUAL_OWNERSHIP,
  Roles.ADMIN_MANUAL_OWNERSHIP_UPDATE,
  Roles.ADMIN_BENCHMARK_OWNERSHIP,
  Roles.ADMIN_BENCHMARK_OWNERSHIP_UPDATE,
  Roles.ADMIN_AUTO_FILL_OWNERSHIP,
  Roles.ADMIN_AUTO_FILL_OWNERSHIP_UPDATE,
  Roles.ADMIN_LIMIT_AUTO_REJECT_RULES,
  Roles.ADMIN_LIMIT_AUTO_REJECT_RULES_UPDATE,
  Roles.ADMIN_BENCHMARK_AUTO_REJECT_RULES,
  Roles.ADMIN_BENCHMARK_AUTO_REJECT_RULES_UPDATE,
  Roles.ADMIN_ALGO_AUTO_REJECT_RULES,
  Roles.ADMIN_ALGO_AUTO_REJECT_RULES_UPDATE,
  Roles.ADMIN_LIMIT_ACCEPTANCE_CONTROLS,
  Roles.ADMIN_LIMIT_ACCEPTANCE_CONTROLS_UPDATE,
  Roles.ADMIN_BENCHMARK_ACCEPTANCE_CONTROLS,
  Roles.ADMIN_BENCHMARK_ACCEPTANCE_CONTROLS_UPDATE,
  Roles.ADMIN_ALGO_ACCEPTANCE_CONTROLS,
  Roles.ADMIN_ALGO_ACCEPTANCE_CONTROLS_UPDATE,
  Roles.ADMIN_BM_ALGO_HEDGING_PROFILES,
  Roles.ADMIN_BM_ALGO_HEDGING_PROFILES_UPDATE,
  Roles.ADMIN_DEFAULT_BM_ALGO_HEDGING_PROFILES,
  Roles.ADMIN_DEFAULT_BM_ALGO_HEDGING_PROFILES_UPDATE,
  Roles.ADMIN_AUTO_FILL_CLIENT_PROFILE,
  Roles.ADMIN_AUTO_FILL_CLIENT_PROFILE_UPDATE,
  Roles.ADMIN_RATE_SOURCE_PROFILE,
  Roles.ADMIN_RATE_SOURCE_PROFILE_UPDATE,
  Roles.ADMIN_AUTO_FILL_CONTROLS,
  Roles.ADMIN_AUTO_FILL_CONTROLS_UPDATE,
  Roles.ADMIN_SYSTEM_CONFIGS,
  Roles.ADMIN_SYSTEM_CONFIGS_UPDATE,
];

function openInNewTab(href) {
  Object.assign(document.createElement("a"), {
    target: "_blank",
    href: href,
  }).click();
}

@Injectable()
export class BfxAppAdminLauncherService {
  private launchInTab: boolean = false;
  private adminWindow: Window = null;

  constructor(
    public systemConfig: SystemConfigPromiseService,
    public configService: BfxConfigService,
    @Inject(SESSION_TIMESTAMP) public sessionTimestamp: string,
  ) {
    this.configService.getUIConfig().then((config: BfxUIConfiguration) => {
      this.launchInTab = config.general.openExternalInTab;
    });
  }

  canView(): boolean {
    return ADMIN_ROLES.some((role) => this.systemConfig.hardHasRole(role));
  }

  getMenuItem(): DashboardMenuItem {
    let menuItem: DashboardMenuItem = new DashboardMenuItem();

    menuItem.label = "Database Administration";
    menuItem.command = () => {
      let uri = `/admin/index.html?v=${new Date().valueOf()}#sessionTimestamp=${
        this.sessionTimestamp
      }`;
      if (!NativeDesktopSupport.isNativeDesktopContainer()) {
        if (this.launchInTab) {
          // this can't be forced- it is ultimately up to the browser to choose a tab based on global config
          this.adminWindow = window.open(uri, "Dashboard");
        } else {
          this.adminWindow = window.open(uri, "Dashboard", "height=800,width=1200,scrollbars=no");
        }
      } else {
        NativeDesktopSupport.launchAdminApp(uri);
      }
    };

    return menuItem;
  }

  /**
   * openfin should use global idle events this is purely for browser path
   *
   * @returns true admin is hidden or no admin window is present
   */
  isAdminWindowHidden(): boolean {
    let hidden = false;

    if (
      !NativeDesktopSupport.isNativeDesktopContainer() &&
      this.adminWindow != null &&
      this.adminWindow.document != null &&
      this.adminWindow.document.visibilityState === "hidden"
    ) {
      hidden = true;
    } else if (!NativeDesktopSupport.isNativeDesktopContainer() && this.adminWindow == null) {
      hidden = true;
    }

    logger.out.debug(`${this.constructor.name}.isAdminWindowHidden() ${hidden}`);

    return hidden;
  }

  closeAdminWindow(): boolean {
    if (this.adminWindow != null) {
      this.adminWindow.close();
      this.adminWindow = null;
      return true;
    }

    return false;
  }
}
